import { RefObject, useRef } from "react";
import { useIsDesktop } from "../../hooks/useIsDesktop";
import {
  motion,
  useMotionValue,
  useScroll,
  useVelocity,
  useSpring,
  useAnimation,
  useTransform,
  useMotionValueEvent,
  useAnimationFrame,
} from "motion/react";
import { MediaItem } from "./MediaItem";
import { useIsOnScreenEffect } from "../../hooks/useIsOnScreenEffect";

export type MediaItem = {
  mediaUrl: string | null;
  contentType: string | null;
};

const MediaGallery = ({
  items,
  itemClassName = "",
  containerRef,
  itemRotations,
}: {
  items: MediaItem[];
  itemClassName?: string;
  containerRef: RefObject<HTMLDivElement>;
  itemRotations?: string[];
}) => {
  const isOnScreenRef = useRef(false);
  useIsOnScreenEffect(
    containerRef,
    (isOnScreen) => {
      isOnScreenRef.current = isOnScreen;
    },
    []
  );
  const isDesktop = useIsDesktop();
  const slideWidth = isDesktop ? 390 : 178;
  const gap = 40;
  const yOffset = isDesktop ? 40 : 20;
  const trackWidth = items.length * (slideWidth + gap);

  const defaultRotations = [
    "-4.293deg",
    "0deg",
    "1.453deg",
    "-3.848deg",
    "5.299deg",
  ];
  const rotations = itemRotations ?? defaultRotations;
  const extendedItems = [...items, ...items];
  const x = useMotionValue(0);
  const { scrollYProgress } = useScroll({
    target: containerRef,
    offset: ["start end", "end start"],
  });
  const scrollVelocity = useVelocity(scrollYProgress);
  const velocity = useSpring(scrollVelocity, { stiffness: 50, damping: 20 });
  const controls = useAnimation();
  const scrollEffect = useTransform(velocity, (v) => v * -10);
  const timeRef = useRef(performance.now());
  const speed = -100 / 1000;

  useAnimationFrame(() => {
    if (!isOnScreenRef.current) return;
    const time = performance.now();
    const deltaTime = time - timeRef.current;
    timeRef.current = time;
    if (scrollYProgress.get() > 0 && scrollYProgress.get() < 1) {
      x.set((x.get() + speed * deltaTime) % -trackWidth);
    }
  });

  useMotionValueEvent(scrollEffect, "change", (latest) => {
    if (!isOnScreenRef.current) return;
    if (scrollYProgress.get() > 0 && scrollYProgress.get() < 1) {
      x.set((x.get() + latest) % -trackWidth);
    }
  });

  const yOffsets = items.map((_id, idx) => {
    if (idx === 0) return 0;
    return `${idx % 2 === 0 ? -yOffset : yOffset}px`;
  });

  return (
    <div className="overflow-hidden">
      <motion.div
        className="flex min-h-[400px] items-center"
        animate={controls}
        style={{ x, gap }}
      >
        {extendedItems.map((item, idx) => (
          <motion.div
            key={`media-gallery-${idx}`}
            className="h-[100px] dsk:h-[220px] rounded-lg flex-shrink-0"
            style={{
              width: slideWidth,
              transform: `rotate(${rotations[idx % items.length]}) translateY(${
                yOffsets[idx % items.length]
              })`,
            }}
          >
            <MediaItem
              onScreenDeterminationElementRef={containerRef}
              className={itemClassName}
              item={{ ...item, url: item.mediaUrl }}
              width={slideWidth}
            />
          </motion.div>
        ))}
      </motion.div>
    </div>
  );
};

export default MediaGallery;
